import { createFileRoute, redirect, useNavigate, Link } from '@tanstack/react-router';
import { m } from 'framer-motion';
import { useState, useEffect, useRef, type FormEvent } from 'react';
import { APP_ROUTES } from '@/constants/routes';
import styles from './login.admin.module.less';
import { AlertCircle } from '@/components/ui/icons';
import { SessionRecoveryAlert } from '@/components/auth/session-recovery-alert';
import { useAdminLogin, useSession } from '@/hooks';
import { logger } from '@/lib/utils/logger';
import {
  resolveAdminLoginRoute,
  shouldAttemptAdminAutoLogin,
} from '@/lib/auth/route-auth-decisions';

// Dev-mode auto-login credentials, read from the local .env. Vite inlines
// every VITE_* literal it can see statically, so an ungated read would bake
// the strings into staging/production bundles even though the auto-login
// effect never runs there. Read only inside the DEV gate so accidental
// staging config ships no credential material (AGENTS.md).
const isDevMode = import.meta.env.DEV;
const DEV_EMAIL = isDevMode ? import.meta.env.VITE_ADMIN_EMAIL : undefined;
const DEV_PASSWORD = isDevMode ? import.meta.env.VITE_ADMIN_PASSWORD : undefined;
// Admin login is dev/staging only — it must never be shown in production.
// Staging deploys as a production-mode build with VITE_APP_ENV=staging, so we
// gate on VITE_APP_ENV (not import.meta.env.PROD, which is also true on
// staging). Fail closed: only exact development/staging values enable the
// route; unset, empty, or malformed values (including a production build
// that omits the variable) disable it (no-mistakes #985).
const isAdminLoginEnabled =
  import.meta.env.VITE_APP_ENV === 'development' || import.meta.env.VITE_APP_ENV === 'staging';

export const Route = createFileRoute('/login/admin')({
  beforeLoad: ({ context }) => {
    const decision = resolveAdminLoginRoute(isAdminLoginEnabled, () => context.authState);
    if (decision.kind === 'redirect') {
      throw redirect({ to: decision.to });
    }
    return { showRecovery: decision.kind === 'recovery' };
  },
  component: AdminLoginPage,
});

function AdminLoginPage() {
  const navigate = useNavigate();
  const sessionQuery = useSession();
  const { showRecovery } = Route.useRouteContext();

  // Redirect if already authenticated
  useEffect(() => {
    if (!sessionQuery.isLoading && sessionQuery.data) {
      logger.info('[AdminLogin]', 'Valid session found, redirecting to home');
      navigate({ to: APP_ROUTES.HOME });
    }
    return undefined;
  }, [navigate, sessionQuery.isLoading, sessionQuery.data]);

  // The form is always empty — credentials are never pre-filled or baked into
  // the bundle. In dev, the auto-login effect below reads the local .env and
  // signs in automatically; on staging the admin types credentials manually
  // (the .env values are for local testing only, not exposed by the app).
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const autoLoginAttempted = useRef(false);

  const adminLogin = useAdminLogin();

  const submitLogin = async (creds: { email: string; password: string }) => {
    setError(null);
    try {
      await adminLogin.mutateAsync(creds);
      navigate({ to: APP_ROUTES.HOME });
    } catch (err) {
      logger.error('[AdminLogin]', 'Login failed', { error: err });
    }
  };

  // Auto-login in dev mode when credentials are available (local-dev only).
  // import.meta.env.DEV is false in staging/production builds, so this never
  // runs there and the (empty) form is used instead.
  useEffect(() => {
    if (
      shouldAttemptAdminAutoLogin({
        showRecovery,
        isDevMode,
        hasEmail: !!DEV_EMAIL,
        hasPassword: !!DEV_PASSWORD,
        alreadyAttempted: autoLoginAttempted.current,
      })
    ) {
      autoLoginAttempted.current = true;
      void submitLogin({ email: DEV_EMAIL!, password: DEV_PASSWORD! });
    }
  }, [showRecovery]);

  // Handle mutation errors
  useEffect(() => {
    if (adminLogin.isError) {
      const err = adminLogin.error as Error | string | null | undefined;
      const errorMessage =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'Failed to login. Please check your credentials and try again.';
      setError(errorMessage);
    }
  }, [adminLogin.isError, adminLogin.error]);

  // Auto-dismiss errors after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submitLogin({ email, password });
  };

  if (showRecovery) {
    return <SessionRecoveryAlert centered />;
  }

  return (
    <div className={styles.adminLoginPage}>
      <div className={styles.adminLoginContainer}>
        {error && (
          <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={styles.errorAlert}>
            <AlertCircle className={styles.errorIcon} />
            <p className={styles.errorMessage}>{error}</p>
          </m.div>
        )}
        <h1 className={styles.title}>Admin sign in</h1>
        <p className={styles.subtitle}>Development and staging environments only.</p>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="admin-email">
              Email
            </label>
            <input
              id="admin-email"
              className={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
            />
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="admin-password">
              Password
            </label>
            <input
              id="admin-password"
              className={styles.input}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className={styles.buttonGroup}>
            <button className={styles.loginButton} type="submit" disabled={adminLogin.isPending}>
              {adminLogin.isPending ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </form>
        <div className={styles.backLink}>
          <Link to={APP_ROUTES.LOGIN} className={styles.link}>
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
